"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export function NpoFundraising({ section }) {
  return (
    <section className="py-10 relative overflow-hidden bg-transparent w-full font-sans">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          
          {/* Left Column: Copywriting */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col gap-4 lg:col-span-5"
          >
            <h2 className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight`}>
              Website ready for{" "}
              <span className="relative inline-block text-amber-400 font-bold">
                <span className="relative z-10">fundraising</span>
                <svg
                  className="absolute -bottom-2 left-0 w-full h-4 overflow-visible opacity-90"
                  viewBox="0 0 100 10"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M 2 8 C 25 4, 60 3, 95 6 C 70 8, 30 9, 5 8 C 28 6, 62 5, 92 7"
                    stroke="#fbbf24"
                    strokeWidth="3.5"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </h2>

            <div className="space-y-4">
              <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.text1 }} />
              <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.text2 }} />
            </div>
          </motion.div>

          {/* Right Column: Overlapping Images */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative flex justify-center py-6 w-full max-w-[850px] mx-auto lg:col-span-7"
          >
            <div className="absolute inset-0 bg-[#fbbf24]/5 rounded-full blur-3xl -z-10 transform scale-75" />
            
            {/* Background overlapping image (Husky website editor) */}
            <motion.div
              initial={{ opacity: 0, y: -20, x: -20 }}
              whileInView={{ opacity: 1, y: -24, x: -32 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="absolute top-0 left-0 w-[88%] p-1 bg-white border border-gray-200 rounded-2xl shadow-xl z-0"
            >
              <Image
                src={section.image2}
                width={700}
                height={450}
                alt="Website builder details"
                className="w-full h-auto rounded-xl object-cover"
              />
            </motion.div>
            
            {/* Foreground image (Donation form website) */}
            <motion.div
              initial={{ opacity: 0, y: 20, x: 20 }}
              whileInView={{ opacity: 1, y: 20, x: 20 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="w-[92%] p-1.5 bg-white border border-gray-200 rounded-2xl shadow-2xl z-10 ml-auto mt-8 hover:scale-[1.01] transition-transform duration-300"
            >
              <Image
                src={section.image1}
                width={800}
                height={500}
                alt="Donation website preview"
                className="w-full h-auto rounded-xl object-cover"
              />
            </motion.div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

export function NpoMembers({ section }) {
  return (
    <section className="py-10 bg-transparent w-full font-sans relative overflow-hidden">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px]">
        
        {/* Centered Heading */}
        <div className="text-center max-w-4xl mx-auto mb-8 relative flex items-center justify-center gap-3">
          <Image
            src={section.redUserIcon}
            width={40}
            height={40}
            alt=""
            className="w-10 h-10 select-none pointer-events-none rotate-[340deg] opacity-75 hidden lg:block"
            style={{ filter: "hue-rotate(340deg) saturate(1.5)" }}
          />
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight`}
          >
            Turn{" "}
            <span className="relative inline-block text-rose-500 font-bold mx-1">
              <span className="relative z-10">visitors</span>
              <svg
                className="absolute -bottom-2 left-0 w-full h-4 overflow-visible opacity-90"
                viewBox="0 0 100 10"
                preserveAspectRatio="none"
              >
                <path
                  d="M 2 8 C 25 4, 60 3, 95 6 C 70 8, 30 9, 5 8 C 28 6, 62 5, 92 7"
                  stroke="#f43f5e"
                  strokeWidth="3.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>{" "}
            into{" "}
            <span className="relative inline-block text-rose-500 font-bold mx-1">
              <span className="relative z-10">members</span>
              <svg
                className="absolute -bottom-2 left-0 w-full h-4 overflow-visible opacity-90"
                viewBox="0 0 100 10"
                preserveAspectRatio="none"
              >
                <path
                  d="M 2 8 C 25 4, 60 3, 95 6 C 70 8, 30 9, 5 8 C 28 6, 62 5, 92 7"
                  stroke="#f43f5e"
                  strokeWidth="3.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </motion.h2>
          <Image
            src={section.redUserIcon}
            width={40}
            height={40}
            alt=""
            className="w-10 h-10 select-none pointer-events-none rotate-[10deg] opacity-75 hidden lg:block"
            style={{ filter: "hue-rotate(340deg) saturate(1.5)" }}
          />
        </div>

        {/* Two Column Grid layout */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 relative items-start">
          <div className="absolute inset-0 bg-[#f43f5e]/5 rounded-full blur-3xl -z-10 transform scale-50" />
          
          {/* Left Column: Invoicing */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex flex-col gap-4"
          >
            <div className="space-y-4">
              <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.leftText1 }} />
              <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.leftText2 }} />
            </div>
            
            <div className="w-full bg-white border border-gray-200 rounded-2xl p-1.5 shadow-xl hover:scale-[1.01] transition-transform duration-300">
              <Image
                src={section.leftImage}
                width={600}
                height={400}
                alt="Donation Invoicing preview"
                className="w-full h-auto rounded-xl object-cover"
              />
            </div>
          </motion.div>

          {/* Right Column: CRM */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="flex flex-col gap-4"
          >
            <div className="w-full bg-white border border-gray-200 rounded-2xl p-1.5 shadow-xl hover:scale-[1.01] transition-transform duration-300">
              <Image
                src={section.rightImage}
                width={600}
                height={400}
                alt="Donors CRM Pipeline"
                className="w-full h-auto rounded-xl object-cover"
              />
            </div>
            
            <div className="pt-2">
              <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.rightText }} />
            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
}

export function NpoEvents({ section }) {
  return (
    <section className="py-10 bg-transparent w-full font-sans relative overflow-hidden">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px]">
        
        {/* Title block */}
        <div className="mb-8">
          <div className="pb-3 mb-2">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55 }}
              className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight`}
            >
              Host events{" "}
              <span className="relative inline-block text-amber-400 font-bold">
                <span className="relative z-10">like a pro</span>
                <svg
                  className="absolute -bottom-2 left-0 w-full h-4 overflow-visible opacity-90"
                  viewBox="0 0 100 10"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M 2 8 C 25 4, 60 3, 95 6 C 70 8, 30 9, 5 8 C 28 6, 62 5, 92 7"
                    stroke="#fbbf24"
                    strokeWidth="3.5"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </motion.h2>
          </div>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="leading-relaxed text-gray-300 max-w-3xl mb-0 text-xl"
            dangerouslySetInnerHTML={{ __html: section.text }}
          />
        </div>

        {/* Mosaic Overlapping Event Images */}
        <div className="relative w-full max-w-[1250px] mx-auto py-8">
          <div className="absolute inset-0 bg-[#fbbf24]/5 rounded-full blur-3xl -z-10 transform scale-75" />
          
          <div className="grid grid-cols-12 gap-8 items-center w-full relative">
            
            {/* Left Image (Main events list dashboard - Huge width) */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="col-span-12 md:col-span-8 lg:col-span-9 p-1.5 bg-white border border-gray-200 rounded-2xl shadow-2xl hover:scale-[1.01] transition-all duration-300"
            >
              <Image
                src={section.images[0]}
                width={900}
                height={600}
                alt="Event Schedule and Listing"
                className="w-full h-auto rounded-xl object-cover"
              />
            </motion.div>
            
            {/* Right Image (Mobile Ticket details card - Narrow width) */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="col-span-12 sm:col-span-6 sm:mx-auto md:mx-0 md:col-span-4 lg:col-span-3 p-1.5 bg-white border border-gray-200 rounded-[28px] shadow-2xl hover:scale-[1.02] transition-all duration-300 relative"
            >
              <Image
                src={section.images[1]}
                width={300}
                height={500}
                alt="Event details preview"
                className="w-full h-auto rounded-[22px] object-cover"
              />
              
              {/* Floating smile emoji positioned exactly at the bottom-right corner of the mobile ticket image */}
              <motion.div
                initial={{ opacity: 0, scale: 0, rotate: -20 }}
                whileInView={{ opacity: 1, scale: 1, rotate: 10 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 180, delay: 0.4 }}
                className="absolute bottom-0 right-0 translate-x-[20%] translate-y-[20%] w-16 h-16 z-20 pointer-events-none drop-shadow-2xl"
              >
                <Image
                  src={section.smileIcon}
                  width={64}
                  height={64}
                  alt="Smile Emoji"
                  className="w-full h-full object-contain"
                />
              </motion.div>
            </motion.div>

          </div>
        </div>

      </div>
    </section>
  );
}

export function NpoCampaigns({ section }) {
  return (
    <section className="py-10 bg-transparent w-full font-sans relative overflow-hidden">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="flex flex-col-reverse lg:flex-row gap-12 lg:gap-20 items-center justify-between">
          
          {/* Left Column: Custom Newsletter Preview Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative flex items-center justify-center py-6 w-full lg:w-1/2 min-h-[380px] lg:min-h-[480px]"
          >
            {/* Soft decorative glow */}
            <div className="absolute inset-0 bg-[#60a5fa]/5 rounded-full blur-[140px] pointer-events-none -z-10" />

            {/* Light circular background matching x_wd_shape_circle */}
            <div className="absolute top-[5%] bottom-[5%] left-[10%] right-[10%] aspect-square bg-white/[0.03] rounded-full -z-10 pointer-events-none select-none" />
            
            <div className="w-[50%] p-1.5 bg-white border border-gray-200 rounded-2xl shadow-2xl hover:scale-[1.01] transition-transform duration-300 relative z-10">
              <Image
                src={section.image}
                width={400}
                height={600}
                alt="NPO Newsletter Builder"
                className="w-full h-auto rounded-xl object-cover"
              />
            </div>
          </motion.div>

          {/* Right Column: Copywriting */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-col gap-4 py-4 w-full lg:w-1/2"
          >
            <h2 className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight`}>
              Build{" "}
              <span className="x_wd_blue_highlight_bold_04 text-white">
                awesome
              </span>{" "}
              campaigns
            </h2>

            <div className="space-y-4">
              <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.text1 }} />
              <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.text2 }} />
              <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.text3 }} />
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
